import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import { useCallback } from 'react';
import { GoToPathButtonContainer } from '../components/goToPathButtonContainer';
import { VersionDisplay } from '../components/versionDisplay';
import { BLUESKY_ACCOUNT_URL } from '../constants/constants';
import { useInstanceId } from '../context/instance/instanceContext';
import { BlueskyIcon } from '../icons/blueskyIcon';
import { setDrawerOpenState } from '../redux/actions/drawerActions';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { DRAWER_BACKGROUND_COLOR, HEADER_SUBTITLE_COLOR, YELLOW_COLOR } from '../styles/colors';
import { getInstanceSelector } from '../utils/uiUtils';
import { isTestEnvironment } from '../utils/utils';
import { AccountExpandableDrawerSection } from './accountExpandableDrawerSection';
import { BadBeatJackpotExpandableDrawerContainer } from './badBeatJackpotExpandableDrawerContainer';
import { ContactUsDrawerButtonContainer } from './contactUsDrawerButtonContainer';
import { GameInfoExpandableDrawerSection } from './gameInfoExpandableDrawerSection';
import { LogOutDrawerButton } from './logOutDrawerButton';
import { OverriddenConfigFlagsDisplayContainer } from './overriddenConfigFlagsDisplayContainer';
import { PlayersExpandableDrawerSection } from './playersExpandableDrawerSection';
import { RankingsExpandableDrawerSection } from './rankingsExpandableDrawerSection';
import { SoundControl } from './soundControl/soundControl';
import { SuperAdminDrawerButtonGroupContainer } from './superAdminControls/superAdminDrawerButtonGroupContainer';
import { ToggleDrawerButton } from './toggleDrawerButton';
import { TourneysExpandableDrawerSection } from './tourneysExpandableDrawerSection';
const TRANSITION_DURATION = isTestEnvironment() ? 0 : undefined;
const DRAWER_CSS = css `
  .MuiDrawer-paper {
    background-color: ${DRAWER_BACKGROUND_COLOR};
    background-image: none;
    border: 1px solid black;
    border-radius: 0 10px 10px 0;
    box-shadow: 8px 8px 0 0 rgba(0, 0, 0, 0.5);
  }
`;
const DRAWER_MAIN_CONTAINER_CSS = css `
  display: flex;
  flex-direction: column;
`;
const HEADER_CSS = css `
  padding: 3px 15px 0 3px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const HEADER_TEXT_CSS = css `
  color: white;
  padding-left: 5px;
  user-select: none;
  cursor: pointer;
  &.MuiTypography-root {
    font-weight: bold;
    line-height: 1em;
  }
`;
const MENU_TEXT_CSS = css `
  color: ${HEADER_SUBTITLE_COLOR};
  font-size: 90%;
`;
const DRAWER_CONTENTS_CSS = css `
  display: flex;
  flex-direction: column;
`;
export const PaddedDrawerContainer = styled.div(({ isHighlighted }) => [
    `
  display: flex;
  flex-direction: column;
  padding: 15px;
  gap: 8px;
`,
    isHighlighted &&
        css `
      border: 3px solid ${YELLOW_COLOR};
      border-radius: 6px;
      margin: 10px 5px;
      padding: 5px;
    `
]);
/**
 * Use less padding on PaddedDrawerContainer components that are inside <ExpandableSection/> components.
 */
export const ExpandableSectionPaddedDrawerContainer = styled(PaddedDrawerContainer) `
  padding: 5px;
`;
export const DrawerControl = ({ render, renderSecondarySection }) => {
    const dispatch = useAppDispatch();
    const isDrawerOpen = useAppSelector((state) => state.drawer.isOpen);
    const instanceId = useInstanceId();
    const closeDrawer = useCallback(() => {
        dispatch(setDrawerOpenState(false));
    }, [dispatch]);
    const onClickBluesky = useCallback(() => {
        closeDrawer();
        window.open(BLUESKY_ACCOUNT_URL, '_blank');
    }, [closeDrawer]);
    return (_jsxs(_Fragment, { children: [_jsx(ToggleDrawerButton, { isOpen: false }), _jsx(Drawer, { "aria-label": 'drawer', open: isDrawerOpen, onClose: closeDrawer, css: DRAWER_CSS, 
                /**
                 * To constrain the drawer to this instance's container (which must have position: relative),
                 * slotProps.backdrop, PaperProps, and ModalProps all must be set with absolute positioning and a function to evaluate to the container element.
                 * See https://github.com/mui-org/material-ui/issues/11749#issuecomment-452987953.
                 */
                slotProps: { backdrop: { style: { position: 'absolute' } } }, PaperProps: { style: { position: 'absolute' } }, ModalProps: {
                    container: () => document.querySelector(getInstanceSelector(instanceId)),
                    style: { position: 'absolute' }
                }, transitionDuration: TRANSITION_DURATION, children: _jsxs("div", { css: DRAWER_MAIN_CONTAINER_CSS, children: [_jsxs("div", { css: HEADER_CSS, children: [_jsx(ToggleDrawerButton, { isOpen: true }), _jsxs(Typography, { variant: 'subtitle1', onPointerDown: closeDrawer, css: HEADER_TEXT_CSS, children: ["TRIPLEJACK", _jsx("div", { css: MENU_TEXT_CSS, children: "MENU" })] })] }), _jsxs("div", { css: DRAWER_CONTENTS_CSS, children: [_jsx(OverriddenConfigFlagsDisplayContainer, {}), /**
                                 * Render children, providing the function to close the drawer after some child action is done:
                                 */ render?.(closeDrawer), _jsx(SoundControl, {}), renderSecondarySection && (_jsx(PaddedDrawerContainer, { children: renderSecondarySection(closeDrawer) })), _jsx(AccountExpandableDrawerSection, { closeDrawer: closeDrawer }), _jsx(GameInfoExpandableDrawerSection, { closeDrawer: closeDrawer }), _jsx(TourneysExpandableDrawerSection, { closeDrawer: closeDrawer }), _jsx(BadBeatJackpotExpandableDrawerContainer, { closeDrawer: closeDrawer }), _jsx(RankingsExpandableDrawerSection, { closeDrawer: closeDrawer }), _jsx(PlayersExpandableDrawerSection, { closeDrawer: closeDrawer }), _jsxs(PaddedDrawerContainer, { children: [_jsx(Button, { onClick: onClickBluesky, startIcon: _jsx(BlueskyIcon, {}), variant: 'outlined', size: 'small', fullWidth: true, children: "@triplejack.com" }), _jsx(ContactUsDrawerButtonContainer, { closeDrawer: closeDrawer }), _jsx(LogOutDrawerButton, { closeDrawer: closeDrawer }), _jsx(VersionDisplay, {}), _jsx(SuperAdminDrawerButtonGroupContainer, { closeDrawer: closeDrawer }), _jsx(GoToPathButtonContainer, { isSuperAdminSection: false, closeDrawer: closeDrawer })] })] })] }) })] }));
};
